/**
 * OrgSearchAutocomplete — find an org to affiliate with (Block 6.B.3 /
 * Q6 disposition: reuse Block 4 searchProviders + client-filter to
 * type='organization').
 *
 * Used by AffiliationEditor's add-new flow. 200ms debounce + min-2-chars
 * matching the LookupPicker discipline.
 */

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import { SearchField } from '@/components/primitives/SearchField';
import { api } from '@/lib/api';
import type { SearchResult } from '@/lib/types';
import { pageReadText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

const DEBOUNCE_MS = 200;
const MIN_CHARS = 2;
const LIMIT = 20;

export interface OrgSearchAutocompleteProps {
  /** Callback when an organization row is picked. */
  onPick: (org: { id: string; display_name: string }) => void;
  /** Callback to cancel/close. */
  onCancel: () => void;
  placeholder?: string;
}

export function OrgSearchAutocomplete({
  onPick,
  onCancel,
  placeholder,
}: OrgSearchAutocompleteProps): ReactNode {
  const { tenantSlug } = useTenant();
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  // A failed search keeps its error and is worded at render, so the search
  // effect does not depend on the contact.
  const [searchError, setSearchError] = useState<unknown>(null);
  const contact = useSupportContact();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounce the query.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Search when debounced query >= MIN_CHARS.
  useEffect(() => {
    const q = debouncedQ.trim();
    if (q.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSearchError(null);
    api
      .searchProviders(tenantSlug, { q, limit: LIMIT })
      .then((r) => {
        if (cancelled) return;
        // Q6: client-filter to organizations.
        setResults(r.rows.filter((row) => row.type === 'organization'));
        setActiveIndex(0);
      })
      .catch((e: unknown) => {
        if (!cancelled) setSearchError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, tenantSlug]);

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = results[activeIndex];
      if (target) onPick({ id: target.id, display_name: target.display_name });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  }

  return (
    <div>
      <label htmlFor="org-merge-search" className="block text-sm text-ink-soft mb-1">
        Search organizations
      </label>
      <SearchField
        ref={inputRef}
        id="org-merge-search"
        value={query}
        onChange={setQuery}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? 'Search by name'}
        aria-autocomplete="list"
        testId="org-merge-search"
      />

      <ul
        role="listbox"
        aria-label="Organization search results"
        className="mt-1 border border-sage-800/[8%] rounded-md bg-white shadow-sm max-h-64 overflow-y-auto"
      >
        {loading && (
          <li className="px-3 py-2 text-sm text-ink-soft" aria-live="polite">
            Searching…
          </li>
        )}
        {!loading && results.length === 0 && (
          <li className="px-3 py-2 text-sm text-ink-soft">
            {query.trim().length === 0
              ? 'Type to search…'
              : query.trim().length < MIN_CHARS
                ? `Type at least ${MIN_CHARS} characters`
                : 'Try another spelling.'}
          </li>
        )}
        {results.map((row, i) => (
          <li
            key={row.id}
            role="option"
            aria-selected={i === activeIndex}
            onMouseDown={(e) => {
              e.preventDefault();
              onPick({ id: row.id, display_name: row.display_name });
            }}
            className={
              'px-3 py-2 text-sm cursor-pointer min-h-11 ' +
              (i === activeIndex ? 'bg-blue-50' : 'hover:bg-sage-50')
            }
          >
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-ink-soft w-10">ORG</span>
              <span>{row.display_name}</span>
            </div>
          </li>
        ))}
      </ul>

      {searchError !== null && (
        <div role="alert" className="mt-1 text-xs text-red-700" data-testid="org-search-error">
          ⚠ {pageReadText(searchError, contact, 'the organizations')}
        </div>
      )}

      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="min-h-11 px-3 py-1 text-sm text-ink-soft hover:bg-sage-50 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
